import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown, HelpCircle } from "lucide-react";
import ShareStrip from "@/components/common/ShareStrip";

const FAQS = [
  {
    q: "How much does a podcast studio session cost?",
    a: "Our sessions start at $150 for a 30-minute Test Run (great to just feel the room). Full podcast sessions start at $600 (Studio Day, 2 hours) and go up to our most popular Creator Launch at $1,050 per episode — that includes studio, editing, short-form clips, and branded intro/outro. Full Production by the hour is $360/hr with a 2-hour minimum.",
  },
  {
    q: "Do you provide the equipment or do I bring my own?",
    a: "Everything is provided. Three Sony 4K cameras, professional 32-bit float audio, studio lighting, dedicated hair & makeup station, and a private wardrobe room. You just show up ready to create.",
  },
  {
    q: "How far in advance do I need to book?",
    a: "For weekday sessions we usually have 5–7 days of lead time. Peak weekends and monthly retainer clients get priority. If you're on a tight timeline, message us and we'll do our best to accommodate — same-week bookings do happen.",
  },
  {
    q: "What's included in editing and post-production?",
    a: "Every Creator Launch and monthly package includes full audio + video editing, color grading, audio cleanup and leveling, branded intro/outro, and short-form vertical clips optimized for Reels/TikTok/YouTube Shorts. Rush 48-hour delivery is available for +$150.",
  },
  {
    q: "Can you help if I've never been on camera before?",
    a: "Absolutely. Every Full Production booking starts with a 30-minute strategy consult to lock in your goals and story. On the day, our team directs the room — lighting, sound, cameras, on-set coaching. You just show up and be yourself; we handle everything else.",
  },
  {
    q: "Do you offer monthly content packages?",
    a: "Yes. Content Partner ($1,550/mo) is our creator retainer — 2 sessions, 2 episodes, 6 clips, plus a monthly strategy call. For brands, Content Engine ($1,500/mo) delivers a full month of ready-to-post content, and Social Command ($3,000/mo) is full end-to-end management including posting and community.",
  },
  {
    q: "How do I know which package is right for me?",
    a: "The fastest way is our 5-question quiz — takes under a minute and we recommend the exact package that fits your goals, output, and budget. Not sure? Book a free discovery call and we'll map it out together.",
  },
  {
    q: "Where is the studio located and what are your hours?",
    a: "We're based in Lakeland, Florida (Polk County). Sessions run by appointment only — we don't do walk-ins. Exact address is shared once your booking is confirmed. We are closed on all US federal holidays plus June 17 and July 26 each year.",
  },
  {
    q: "What's the Small Business Spotlight Podcast — and is it really free?",
    a: "Yes, 100% free. It's our way of giving back to Central Florida's founders, creators, and community leaders. Shoots happen Saturdays only at 12 PM or 5 PM. In exchange we ask guests to follow SB Spotlight + SB Media Studio on social and invite their Facebook contacts to follow the show. That's it. Click 'Request your spotlight' in the Spotlight section to apply.",
  },
  {
    q: "What's your cancellation and reschedule policy?",
    a: "Reschedules are free up to 72 hours before the session. Cancellations inside 72 hours forfeit 50% of the booking fee to cover the reserved crew time; inside 24 hours we retain the full booking. Monthly retainer sessions can be moved within the same month at no cost.",
  },
];

const FaqItem = ({ item, index, open, onToggle }) => (
  <div
    data-testid={`faq-item-${index}`}
    className={`rounded-2xl border transition-colors ${
      open
        ? "border-[#00f0ff]/40 bg-[#00f0ff]/[0.04]"
        : "border-white/10 bg-white/[0.02] hover:border-white/20"
    }`}
  >
    <button
      type="button"
      onClick={onToggle}
      data-testid={`faq-toggle-${index}`}
      className="w-full flex items-start justify-between gap-4 text-left px-5 py-4"
    >
      <span className="flex items-start gap-3 flex-1">
        <span className="font-mono-tight text-[10px] uppercase tracking-[0.24em] text-[#00f0ff] mt-1 flex-shrink-0">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="font-display font-semibold text-[15px] md:text-base leading-snug text-white">
          {item.q}
        </span>
      </span>
      <ChevronDown
        size={18}
        className={`text-[#a395c3] flex-shrink-0 transition-transform ${
          open ? "rotate-180 text-[#00f0ff]" : ""
        }`}
      />
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="body"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <div className="px-5 pb-5 pl-[52px] text-[14px] leading-relaxed text-[#cfc4e0]">
            {item.a}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const PricingFAQModal = ({ open, onClose }) => {
  const [openIndex, setOpenIndex] = useState(0);
  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="faq-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="pricing-faq-modal"
        className="fixed inset-0 z-[100] overflow-y-auto bg-black/85 backdrop-blur-md"
      >
        <div className="min-h-full flex items-start justify-center px-3 py-6 sm:py-10">
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl my-4 rounded-3xl bg-[#0a0612] border border-white/10 shadow-[0_30px_120px_rgba(0,240,255,0.18)]"
          >
            <div className="flex items-start justify-between gap-4 px-6 md:px-8 pt-7 pb-5 bg-[#0a0612] rounded-t-3xl">
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#00f0ff] mb-2 flex items-center gap-2">
                  <HelpCircle size={12} /> Pricing Q&amp;A
                </div>
                <h3 className="font-display font-black text-2xl md:text-[28px] tracking-tight">
                  The 10 questions we get{" "}
                  <span className="italic font-light text-[#a395c3]">most.</span>
                </h3>
                <div className="text-[#a395c3] text-sm mt-1.5 max-w-md">
                  Answered straight — no fluff. Tap any question to expand.
                </div>
              </div>
              <ShareStrip campaign="pricing_faq" title="SB Media Studio — pricing FAQ" text="Every pricing question answered — podcast, video, UGC in Lakeland, FL." href="/#packages" className="mr-1" />
              <button
                type="button"
                onClick={onClose}
                data-testid="pricing-faq-close"
                aria-label="Close FAQ"
                className="w-9 h-9 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center hover:bg-white/[0.12]"
              >
                <X size={14} />
              </button>
            </div>

            <div className="px-4 md:px-6 pb-8 pt-2 space-y-3">
              {FAQS.map((item, i) => (
                <FaqItem
                  key={item.q}
                  item={item}
                  index={i}
                  open={openIndex === i}
                  onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                />
              ))}

              <div className="mt-6 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-sm text-[#a395c3]">
                  Still have a question we didn&apos;t cover?
                </div>
                
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="pricing-faq-cta-contact"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#00f0ff] text-[#05050a] text-sm font-semibold hover:bg-white transition-colors"
                >
                  Contact us
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PricingFAQModal;
